import { useCallback } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

interface BpmnViewerToolbarProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitViewport: () => void;
  onExportSvg: () => Promise<string | null>;
  onExportPng: () => Promise<Blob | null>;
  processDefinitionKey?: string;
}

export function BpmnViewerToolbar({
  onZoomIn,
  onZoomOut,
  onFitViewport,
  onExportSvg,
  onExportPng,
  processDefinitionKey,
}: BpmnViewerToolbarProps) {
  const handleExportSvg = useCallback(async () => {
    const svg = await onExportSvg();
    if (!svg) return;

    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${processDefinitionKey || 'diagram'}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [onExportSvg, processDefinitionKey]);

  const handleExportPng = useCallback(async () => {
    const blob = await onExportPng();
    if (!blob) return;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${processDefinitionKey || 'diagram'}.png`;
    a.click();
    URL.revokeObjectURL(url);
  }, [onExportPng, processDefinitionKey]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        p: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Tooltip title="Vergrößern">
        <IconButton size="small" onClick={onZoomIn}>
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Verkleinern">
        <IconButton size="small" onClick={onZoomOut}>
          <ZoomOutIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Einpassen">
        <IconButton size="small" onClick={onFitViewport}>
          <FitScreenIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      <Tooltip title="Als SVG exportieren">
        <IconButton size="small" onClick={handleExportSvg}>
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Als PNG exportieren">
        <IconButton size="small" onClick={handleExportPng}>
          <ImageIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
